/**
 * Plans API service
 * Handles communication with the backend for plans
 */

import { apiGet, apiPost } from './api';
import type { AIGeneratedQuestion, AIAnalysis } from '../data/publicPlans';

export interface Plan {
	id: number;
	user_id: number;
	title: string;
	description?: string;
	style?: string;
	location?: string;
	// Questionnaire fields
	garden_size?: number;
	garden_shape?: 'rectangular' | 'l-shaped' | 'irregular' | 'square' | 'circular';
	garden_components?: string[] | any; // JSON array of selected components
	needs_wants?: string;
	maintenance_level?: 'low' | 'medium' | 'high';
	children_pets?: 'none' | 'children' | 'pets' | 'both';
	budget_min?: number;
	budget_max?: number;
	timeline?: string;
	project_start_date?: string; // ISO date string
	contact_email?: string;
	household_size?: number;
	garden_users?: number;
	plant_density_preference?: 'minimal' | 'moderate' | 'dense';
	project_focus?: 'planting_only' | 'construction_hardscape_only' | 'mixed_planting_construction';
	garden_uses?: string[];
	hosting_max_people?: number;
	kids_count?: number;
	kids_ages?: string;
	pets_description?: string;
	// Plan metadata
	status: 'draft' | 'published' | 'closed' | 'archived';
	plan_type: 'professional' | 'diy';
	source?: 'organic' | 'invite';
	visibility?: 'marketplace' | 'private';
	exclusive_company_id?: number | null;
	moderation_status?: 'visible' | 'hidden' | 'deleted';
	before_image_url?: string;
	after_image_url?: string;
	ai_plan_details?: any; // Now only stores AI-generated content, not questionnaire
	generation_duration_seconds?: number;
	revisions?: any[]; // History of AI-generated designs
	created_at: string;
	updated_at: string;
	published_at?: string;
	/** Set on GET /companies/my/invite-plans (EXISTS subqueries) */
	has_client_invite?: number | string | boolean;
	has_linked_seeker?: number | string | boolean;
}

export interface CreatePlanPayload {
	user_id: number;
	title: string;
	description?: string;
	style?: string;
	location?: string;
	// Questionnaire fields
	garden_size?: number;
	garden_shape?: 'rectangular' | 'l-shaped' | 'irregular' | 'square' | 'circular';
	garden_components?: string[] | any; // JSON array of selected components
	needs_wants?: string;
	maintenance_level?: 'low' | 'medium' | 'high';
	children_pets?: 'none' | 'children' | 'pets' | 'both';
	budget_min?: number;
	budget_max?: number;
	timeline?: string;
	project_start_date?: string; // ISO date string
	contact_email?: string;
	household_size?: number;
	garden_users?: number;
	plant_density_preference?: 'minimal' | 'moderate' | 'dense';
	project_focus?: 'planting_only' | 'construction_hardscape_only' | 'mixed_planting_construction';
	garden_uses?: string[];
	hosting_max_people?: number;
	kids_count?: number;
	kids_ages?: string;
	pets_description?: string;
	// Plan metadata
	status?: 'draft' | 'published' | 'closed' | 'archived';
	plan_type?: 'professional' | 'diy';
	source?: 'organic' | 'invite';
	visibility?: 'marketplace' | 'private';
	exclusive_company_id?: number | null;
	invite_code?: string;
	before_image_url?: string;
	after_image_url?: string;
	ai_plan_details?: any; // Now only stores AI-generated content, not questionnaire
	generation_duration_seconds?: number;
	// AI Questionnaire fields
	ai_questions?: AIGeneratedQuestion[];
	ai_answers?: Record<string, any>;
	ai_analysis?: AIAnalysis;
}

export interface UpdatePlanContentPayload {
	title?: string;
	description?: string;
	style?: string;
	location?: string;
	// Questionnaire fields
	garden_size?: number;
	garden_shape?: 'rectangular' | 'l-shaped' | 'irregular' | 'square' | 'circular';
	garden_components?: string[] | any;
	needs_wants?: string;
	maintenance_level?: 'low' | 'medium' | 'high';
	children_pets?: 'none' | 'children' | 'pets' | 'both';
	budget_min?: number;
	budget_max?: number;
	timeline?: string;
	project_start_date?: string;
	contact_email?: string;
	household_size?: number;
	garden_users?: number;
	plant_density_preference?: 'minimal' | 'moderate' | 'dense';
	project_focus?: 'planting_only' | 'construction_hardscape_only' | 'mixed_planting_construction';
	garden_uses?: string[];
	hosting_max_people?: number;
	kids_count?: number;
	kids_ages?: string;
	pets_description?: string;
	// AI / image fields
	before_image_url?: string;
	after_image_url?: string;
	ai_plan_details?: any;
	ai_questions?: AIGeneratedQuestion[];
	ai_answers?: Record<string, any>;
	ai_analysis?: AIAnalysis;
	append_previous_revision?: boolean;
}

/**
 * Get all plans (optionally filtered by user_id)
 */
export async function getPlans(
	userId?: number,
	options?: { includeHidden?: boolean; includeDeleted?: boolean; status?: Plan['status'] }
): Promise<Plan[]> {
	const params = new URLSearchParams();
	if (userId) params.set('user_id', String(userId));
	if (options?.includeHidden) params.set('include_hidden', 'true');
	if (options?.includeDeleted) params.set('include_deleted', 'true');
	if (options?.status) params.set('status', options.status);
	const endpoint = params.size > 0 ? `/plans?${params.toString()}` : '/plans';
	return apiGet<Plan[]>(endpoint);
}

/**
 * Get a specific plan by ID
 */
export async function getPlanById(planId: number): Promise<Plan> {
	return apiGet<Plan>(`/plans/${planId}`);
}

/**
 * Create a new plan
 */
export async function createPlan(payload: CreatePlanPayload): Promise<Plan> {
	return apiPost<Plan>('/plans', payload);
}

/**
 * Update plan moderation status
 */
export async function updatePlanModerationStatus(planId: number, moderationStatus: 'visible' | 'hidden' | 'deleted'): Promise<Plan> {
	const { apiPut } = await import('./api');
	return apiPut<Plan>(`/plans/${planId}`, { moderation_status: moderationStatus });
}

/**
 * Update plan status (e.g. draft -> published)
 */
export async function updatePlanStatus(planId: number, status: 'draft' | 'published' | 'closed' | 'archived'): Promise<Plan> {
	const { apiPut } = await import('./api');
	return apiPut<Plan>(`/plans/${planId}`, { status });
}

/**
 * Update plan content (questionnaire + AI data) without publishing.
 */
export async function updatePlanContent(planId: number, payload: UpdatePlanContentPayload): Promise<Plan> {
	const { apiPut } = await import('./api');
	return apiPut<Plan>(`/plans/${planId}`, {
		update_content: true,
		...payload
	});
}

/** Payload for one revision (AI content + optional edit label) */
export interface AppendRevisionPayload {
	photoAfterUrl: string;
	planDescription: string;
	keyFeatures: string[];
	instructions: string[];
	timeline_tasks?: Array<{
		title: string;
		description: string;
		dayStart: number;
		dayEnd: number;
		dependsOnSteps?: number[];
		required_skills_for_step?: string[];
		required_gardeners_for_step?: number;
	}>;
	tools?: Array<{ name: string; description: string }> | string[];
	pricingBreakdown: { min: number; mid: number; max: number; materials: any[]; labor: any[] };
	generatedAt: string;
	required_skills?: string[];
	recommended_team_size?: number;
	editInstructions?: string;
}

/**
 * Append a new revision (after "Regenerate with changes") and set as current
 */
export async function appendPlanRevision(planId: number, revision: AppendRevisionPayload): Promise<Plan> {
	const { apiPut } = await import('./api');
	return apiPut<Plan>(`/plans/${planId}`, { append_revision: revision });
}

/**
 * Restore a revision (set current view to that revision)
 */
export async function restorePlanRevision(planId: number, revisionIndex: number): Promise<Plan> {
	const { apiPut } = await import('./api');
	return apiPut<Plan>(`/plans/${planId}`, { restore_revision: revisionIndex });
}

/**
 * Delete a plan (soft delete)
 */
export async function deletePlan(planId: number): Promise<void> {
	const { apiDelete } = await import('./api');
	await apiDelete(`/plans/${planId}`);
}

